/**
 * 多店店长切店器 (072 挂载; 2026-07-09 Ricky 模拟赵乐 "网页端没有看到切换门店")。
 *
 * 与 mp store-home 顶行胶囊同语义: 店长本人 + 多店挂载才显示; 选店调
 * POST /auth/switch-store 换发 JWT (身份级切换, 排班/考勤/审批全随店),
 * 换 token 后整页回首页重载。区经/owner 的"查看门店"切店器 (视图级) 不受影响。
 */
import React, { useState } from 'react';
import { App, Dropdown } from 'antd';

import { AuthService } from '../../.generated/api-client';
import type { AdminProfile } from '../layouts/AdminScopeContext';
import { asRecord, setAdminToken } from '../services/api';
import { shortStoreName } from '../utils/storeName';

export async function switchManagerStoreSession(
  storeId: number,
): Promise<void> {
  const resp = await AuthService.switchStoreEndpointApiV1AuthSwitchStorePost({
    requestBody: { store_id: storeId },
  });
  const data = asRecord(asRecord(resp).data ?? resp);
  const jwt = data.jwt;
  if (typeof jwt !== 'string' || jwt.length === 0) {
    throw new Error('missing switched token');
  }
  setAdminToken(jwt);
}

const ManagerStoreSwitch: React.FC<{ profile: AdminProfile | null }> = ({ profile }) => {
  const { message } = App.useApp();
  const [switching, setSwitching] = useState(false);

  if (
    !profile ||
    profile.isSuperAdmin ||
    profile.role !== 'store_manager' ||
    profile.managedStores.length < 2
  ) {
    return null;
  }

  const current = profile.managedStores.find((m) => m.storeId === profile.storeId);
  const currentLabel = shortStoreName(current?.storeNameCn ?? profile.storeName ?? '当前门店');

  const onPick = async (storeId: number, label: string) => {
    if (switching || storeId === profile.storeId) return;
    setSwitching(true);
    try {
      await switchManagerStoreSession(storeId);
      message.success(`已切到${label}`);
      // 身份上下文整体切换: 留在当前页整页重载 (Ricky 2026-07-09 "在哪个页面切
      // 就应该留在这个页面, 只是门店被刷新了"), 页面数据随新店 token 重新拉取。
      window.location.reload();
    } catch {
      message.error('切换失败，请重试');
      setSwitching(false);
    }
  };

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: profile.managedStores.map((m) => {
          const label = shortStoreName(m.storeNameCn);
          return {
            key: String(m.storeId),
            label: m.storeId === profile.storeId ? `${label}（当前）` : label,
            disabled: m.storeId === profile.storeId,
          };
        }),
        onClick: ({ key }) => {
          const target = profile.managedStores.find((m) => String(m.storeId) === key);
          if (target) void onPick(target.storeId, shortStoreName(target.storeNameCn));
        },
      }}
    >
      <button
        type="button"
        className="mgr-store-switch"
        disabled={switching}
        data-testid="manager-store-switch"
        aria-label={`查看门店：${currentLabel}`}
      >
        <span className="mgr-store-switch__name">{currentLabel}</span>
        <span className="mgr-store-switch__caret">▾</span>
      </button>
    </Dropdown>
  );
};

export default ManagerStoreSwitch;
